import env from "@perfectserve/env-ps";
import { HttpService } from "@perfectserve/http-ps";

import { PERMISSIONS } from "../constants/permissions";
import { getEnvConfig, PSConfig } from "../hooks/useEnv";
import { createUserConfiguration as mapUserConfig } from "../utils/userConfigurationMapper";

const PERFECT_SERVE_EMPLOYEE_ROLE = PERMISSIONS.PERFECT_SERVE_EMPLOYEE;

export interface UserRole {
  facilityName: string;
  roleName: string;
  facilityGuid: string;
  roleId: string;
  messagingKey?: {
    System: string;
    Id: string;
  };
  scopes: string[];
  isPractitioner?: boolean;
  displayName?: string;
  specialtyName?: string;
  practiceName?: string;
  departmentName?: string;
  facilityId?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface UserProfile {
  id: number;
  uid: string;
  account_id: number;
  account_name: string;
  account_uid: string;
  contact_id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  upn?: string; // User Principal Name from JWT token
  permissions?: any; // Raw permissions API response data
  timezone: string | null;
  title: string | null;
  username: string;
  featureFlags: Record<string, boolean>;
  profile_picture?: string | null;
  roles: UserRole[];
}

class UserService {
  private config: PSConfig;

  constructor() {
    // Load config using useEnv utility
    const envConfig = getEnvConfig();
    this.config = envConfig || ({} as PSConfig);

    // Check for one-time migration flag
    const migrationFlag = localStorage.getItem("ps_permissions_migrated");
    if (!migrationFlag) {
      this.forceClearCachedProfile();
      localStorage.setItem("ps_permissions_migrated", "true");
    }

    // UserService is now a pure data access layer - no state management
  }

  /**
   * Load user profile from localStorage
   */
  public loadFromStorage(): UserProfile | null {
    const storedProfile = localStorage.getItem("ps_userConfiguration");
    if (storedProfile) {
      try {
        const profile = JSON.parse(storedProfile);

        // Handle migration from old permission format (Record<string, boolean>) to new format (string[])
        if (profile.permissions && typeof profile.permissions === "object" && !Array.isArray(profile.permissions)) {
          // Convert old format to new format
          profile.permissions = Object.keys(profile.permissions).filter((key) => profile.permissions[key]);
        }

        // Ensure permissions is always an array
        if (!Array.isArray(profile.permissions)) {
          profile.permissions = [];
        }

        return profile;
      } catch {
        localStorage.removeItem("ps_userConfiguration");
      }
    }
    return null;
  }

  /**
   * Fetch user profile from Directory API
   */
  public async fetchProfile(identityId: string, accessToken: string): Promise<UserProfile> {
    // Use the imported HTTP service

    // Create HTTP service instance with current token
    const httpService = new HttpService({
      getAuthToken: () => accessToken,
      envConfig: {
        cspApi: env.cspApi || "",
        directoryApi: env.directoryApi || "",
      },
      onAuthError: () => {}, // No auth error handling needed for profile fetch
    });
    const response = await httpService.get(`contacts/byIdentityId/${identityId}`, {
      baseURL: env.directoryApi || "",
    });
    const profileData = response.data;

    // Get basic user info for profile creation
    const practitionerContact = Array.isArray(profileData) && profileData.find((c) => c.ContactType === "Practitioner");

    // Parse roles from Directory API response
    const { practitionerRole, careTeamRoles } = this.parseRoles(profileData);
    const allRoles = practitionerRole ? [practitionerRole, ...careTeamRoles] : careTeamRoles;

    // Create user profile from Directory API response
    const userProfile: UserProfile = {
      id: practitionerContact?.Properties?.Id || 1,
      uid: practitionerContact?.Properties?.IdentityId || identityId,
      account_id: 1,
      account_name: practitionerContact?.Properties?.PracticeName || "Unknown Account",
      account_uid: practitionerContact?.Properties?.PracticeId || "unknown",
      contact_id: practitionerContact?.ParentContactId || "unknown",
      firstName: practitionerContact?.Properties?.FirstName || "Unknown",
      lastName: practitionerContact?.Properties?.LastName || "User",
      displayName:
        practitionerContact?.DisplayName ||
        `${practitionerContact?.Properties?.FirstName || "Unknown"} ${practitionerContact?.Properties?.LastName || "User"}`,
      email: "unknown@example.com", // Will be filled from ID token
      permissions: [], // Will be loaded separately
      timezone: null,
      title: practitionerContact?.Properties?.SpecialtyName || null,
      username: "unknown", // Will be filled from ID token
      featureFlags: {},
      profile_picture: null,
      roles: allRoles,
    };

    return userProfile;
  }

  /**
   * Enhance user profile with ID token information
   */
  public enhanceProfileWithIdToken(userProfile: UserProfile, userInfo: any): UserProfile {
    // Update profile with ID token data
    return {
      ...userProfile,
      email: userInfo.email || userProfile.email,
      upn: userInfo.upn, // Extract UPN from JWT token
    };
  }

  /**
   * Parse roles from Directory API response
   */
  private parseRoles(profileData: any[]): {
    practitionerRole: UserRole | null;
    careTeamRoles: UserRole[];
  } {
    // Process directory API response

    const careTeamRoles: UserRole[] = [];
    let practitionerRole: UserRole | null = null;

    if (Array.isArray(profileData)) {
      profileData.forEach((contact: any) => {
        // Check for Practitioner contact
        if (contact.ContactType === "Practitioner") {
          practitionerRole = {
            facilityName: contact.Properties?.PracticeName || "Unknown Practice",
            roleName: contact.Properties?.SpecialtyName || "Practitioner",
            facilityGuid: contact.Properties?.PracticeId || "",
            roleId: contact.ParentContactId || "",
            messagingKey: contact.MessagingKey,
            scopes: contact.Scopes || [],
            isPractitioner: true,
            displayName: contact.DisplayName || "Unknown Practitioner",
            specialtyName: contact.Properties?.SpecialtyName || null,
            practiceName: contact.Properties?.PracticeName || null,
            departmentName: contact.Properties?.DepartmentName || null,
          };
        }

        // Check for CareTeamMember contacts with subcontacts (facility roles)
        if (contact.ContactType === "CareTeamMember" && contact.Subcontacts) {
          contact.Subcontacts.forEach((subcontact: any) => {
            if (subcontact.Properties?.FacilityName && subcontact.Properties?.RoleName) {
              const newRole = {
                facilityName: subcontact.Properties.FacilityName,
                roleName: subcontact.Properties.RoleName,
                facilityGuid: subcontact.Properties.FacilityGuid || "",
                roleId: subcontact.Properties.RoleId || "",
                messagingKey: subcontact.MessagingKey,
                scopes: subcontact.Scopes || [],
                isPractitioner: false,
                departmentName: subcontact.Properties?.DepartmentName || null,
              };

              careTeamRoles.push(newRole);
            }
          });
        }
      });
    }

    // Return parsed roles

    return { practitionerRole, careTeamRoles };
  }

  /**
   * Store user profile to localStorage
   */
  public storeProfile(userProfile: UserProfile): void {
    localStorage.setItem("ps_userConfiguration", JSON.stringify(userProfile));
  }

  /**
   * Clear user profile from storage
   */
  public clearProfile(): void {
    // Clean up any remaining cached profile data
    localStorage.removeItem("ps_userConfiguration");
  }

  /**
   * Create UserConfiguration for Angular Elements
   */
  public createUserConfiguration(userProfile: UserProfile, selectedRole: UserRole | null): any {
    // Import the mapper to avoid circular dependencies

    return mapUserConfig(userProfile, selectedRole, userProfile.roles);
  }

  /**
   * Force clear cached profile to ensure clean migration
   */
  public forceClearCachedProfile(): void {
    localStorage.removeItem("ps_userConfiguration");
  }

  /**
   * Load permissions from the privileges API
   */
  public async loadPermissions(accessToken: string): Promise<string[]> {
    try {
      // Use the imported HTTP service

      // Create HTTP service instance with current token
      const httpService = new HttpService({
        getAuthToken: () => accessToken,
        envConfig: { cspApi: this.config.cspApi || "" },
        onAuthError: () => {}, // No auth error handling needed for permissions fetch
      });

      // Use cspApi base URL from env-config (no fallback - must be configured)
      if (!this.config.cspApi) {
        console.error("CSP API URL not configured in environment");
        return [];
      }

      const response = await httpService.get<string[]>(`/api/privilege/permissions`, {
        baseURL: this.config.cspApi,
        skipAutoLogout: true,
      });

      return response.data;
    } catch (error: any) {
      // Handle 403 Forbidden as "no permissions configured" - this is normal, not an error
      if (error.status === 403) {
        return [];
      }

      // Handle 401 as authentication issue
      if (error.status === 401) {
        return [];
      }

      // For other errors, throw
      throw error;
    }
  }

  /**
   * Check if user has specific permission (pure function)
   */
  public hasPermission(permissions: string[], permission: string): boolean {
    if (!permission || typeof permission !== "string") {
      return false;
    }

    if (!Array.isArray(permissions)) {
      return false;
    }

    return permissions.includes(permission);
  }

  /**
   * Check if user is a PerfectServe employee (pure function)
   */
  public isPerfectServeEmployee(permissions: string[]): boolean {
    return this.hasPermission(permissions, PERFECT_SERVE_EMPLOYEE_ROLE);
  }

  /**
   * Get practitioner role (pure function)
   */
  public getPractitionerRole(userProfile: UserProfile | null): UserRole | null {
    return userProfile?.roles.find((role) => role.isPractitioner) || null;
  }

  /**
   * Get care team roles (pure function)
   */
  public getCareTeamRoles(userProfile: UserProfile | null): UserRole[] {
    return userProfile?.roles.filter((role) => !role.isPractitioner) || [];
  }

  /**
   * Get all user roles (pure function)
   */
  public getRoles(userProfile: UserProfile | null): UserRole[] {
    return userProfile?.roles || [];
  }

  /**
   * Extract user information from ID token
   */
  public extractUserFromIdToken(idToken: string): any {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = idToken.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT token format");
    }

    // Decode the payload (second part)
    const payload = parts[1];
    if (!payload) {
      throw new Error("Invalid JWT token payload");
    }

    // Add padding if needed for base64 decoding
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);

    // Decode base64 and parse JSON
    const decodedPayload = atob(paddedPayload.replace(/-/g, "+").replace(/_/g, "/"));
    const userInfo = JSON.parse(decodedPayload);

    return userInfo;
  }
}

export const userService = new UserService();
